<template>
  <div class="user-dropdown">
    <button class="avatar-button" @click.stop="toggle">
      <span class="avatar-wrap">
        <img :src="user.avatarUrl" class="avatar" :class="{ 'avatar-vip': isVip }" alt="avatar" />
        <span v-if="isVip" class="vip-badge vip-badge--mini">
          <Icon name="material-symbols:workspace-premium" size="10" />
        </span>
      </span>
      <Icon name="material-symbols:expand-more" size="18" />
    </button>
    <div v-if="open" class="menu">
      <button class="menu-item" @click="onSelect('my-page')">
        <Icon name="material-symbols:person" size="18" /> {{ $t('dropdown.myPage') }}
      </button>
      <button class="menu-item" @click="onSelect('pay')">
        <Icon name="material-symbols:workspace-premium" size="18" /> {{ $t('dropdown.upgrade') }}
      </button>
      <button class="menu-item" @click="onSelect('history')">
        <Icon name="material-symbols:history" size="18" /> {{ $t('dropdown.history') }}
      </button>
      <div class="divider"></div>
      <button class="menu-item" @click="onSelect('logout')">
        <Icon name="material-symbols:logout" size="18" /> {{ $t('dropdown.logout') }}
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'

interface UserInfo { id: string; name: string; avatarUrl: string }
interface Props { user: UserInfo; open: boolean; isVip?: boolean }
const props = withDefaults(defineProps<Props>(), { isVip: false })
const emit = defineEmits<{ (e: 'toggle'): void; (e: 'select', key: 'my-page' | 'pay' | 'history' | 'logout'): void }>()

const toggle = () => emit('toggle')
const onSelect = (key: 'my-page' | 'pay' | 'history' | 'logout') => emit('select', key)

const handleOutside = (e: MouseEvent) => {
  const root = (e.target as HTMLElement).closest('.user-dropdown')
  // 仅在下拉已打开时，点击外部才触发关闭，避免误开
  if (!root && props.open) emit('toggle')
}

onMounted(() => document.addEventListener('click', handleOutside))
onUnmounted(() => document.removeEventListener('click', handleOutside))
</script>

<style scoped lang="scss">
.user-dropdown {
  position: relative;
}
.avatar-button {
  display: flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid #e5e7eb;
  padding: 4px 8px;
  border-radius: 999px;
  cursor: pointer;
}
.avatar-wrap {
  position: relative;
  display: inline-flex;
}
.avatar {
  width: 28px;
  height: 28px;
  border-radius: 50%;
}
.avatar-vip {
  /* 外环方案B：内白环 + 外金环 */
  box-shadow:
    0 0 0 2px #fff,
    0 0 0 4px rgba(251, 191, 36, 0.95); /* amber-400 */
}
.vip-badge {
  position: absolute;
  right: -2px;
  bottom: -2px; /* 方案B：小角标回到底部，更低调 */
  width: 14px;
  height: 14px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%);
  border: 1px solid #fff; /* 细白描边 */
  box-shadow: 0 1px 3px rgba(245, 158, 11, 0.35);
  pointer-events: none;
  z-index: 1;
}
.vip-badge--mini :deep(.iconify) {
  color: #fff;
}
.menu {
  position: absolute;
  right: 0;
  top: calc(100% + 6px);
  background: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 8px 24px rgba(0,0,0,0.08);
  min-width: 180px;
  padding: 6px 0;
  z-index: 1000;
}
.menu-item {
  width: 100%;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border: 0;
  background: transparent;
  text-align: left;
  cursor: pointer;
}
.menu-item:hover { background: #f3f4f6; }
.divider { height: 1px; background: #e5e7eb; margin: 6px 0; }
</style>
